package songwritersnotebook.app.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.ArrowForward
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import songwritersnotebook.app.data.ChordDefinition
import songwritersnotebook.app.data.GuitarTab
import songwritersnotebook.app.data.UserPresence
import songwritersnotebook.app.viewmodels.TabEditorViewModel
import songwritersnotebook.app.viewmodels.TabInputMode
import org.jetbrains.compose.resources.painterResource
import songwritersnotebookapp.composeapp.generated.resources.Res
import songwritersnotebookapp.composeapp.generated.resources.ic_guitar

@Composable
fun TabEditorSheet(
    initialTab: GuitarTab,
    availableChords: List<ChordDefinition>,
    onDismiss: () -> Unit,
    onSave: (GuitarTab) -> Unit,
    canUseTabs: Boolean,
    onUpgrade: () -> Unit,
    legendItems: List<String> = emptyList(),
    isLeftHanded: Boolean = false,
    tuning: List<String> = songwritersnotebook.app.utils.ChordUtils.DEFAULT_GUITAR_TUNING,
    capo: Int = 0,
    onPlayNotes: (List<String>) -> Unit = {},
    // Recordings
    recordings: List<songwritersnotebook.app.data.RecordingMetadata> = emptyList(),
    lineRecordings: List<songwritersnotebook.app.data.RecordingMetadata> = emptyList(),
    playingRecordingId: String? = null,
    onRecordNew: (songwritersnotebook.app.data.GuitarTab) -> Unit = { _ -> },
    onPlayRecording: (songwritersnotebook.app.data.RecordingMetadata) -> Unit = {},
    onSelectExisting: (songwritersnotebook.app.data.RecordingMetadata, songwritersnotebook.app.data.GuitarTab) -> Unit = { _, _ -> },
    onUnlink: (songwritersnotebook.app.data.GuitarTab) -> Unit = { _ -> },
    onTabChange: (songwritersnotebook.app.data.GuitarTab) -> Unit = { _ -> },
    songId: String = "",
    targetId: String = "",
    tabId: String = ""
) {
    val viewModel: TabEditorViewModel = viewModel(
        key = initialTab.id,
        factory = viewModelFactory { initializer { TabEditorViewModel(initialTab, availableChords, songId, targetId, tabId, onTabChange) } }
    )
    val uiState by viewModel.uiState.collectAsState()
    var showSettings by remember { mutableStateOf(false) }
    var showRecordings by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        viewModel.playTrigger.collect {
            val selection = viewModel.uiState.value.fretboardSelection
            val notesToPlay = selection.mapNotNull { (stringIndex, fret) ->
                val note = getTabNoteForFret(stringIndex, fret, tuning, capo)
                if (note == "---") null else note
            }
            if (notesToPlay.isNotEmpty()) {
                onPlayNotes(notesToPlay)
            }
        }
    }

    if (showSettings) {
        TabSettingsDialog(
            currentSubdivisions = uiState.tab.subdivisions,
            currentBeats = uiState.tab.beats,
            onDismiss = { showSettings = false },
            onConfirm = { sub, beats ->
                viewModel.updateTabSettings(sub, beats)
                showSettings = false
            }
        )
    }
    
    if (showRecordings) {
        Dialog(onDismissRequest = { showRecordings = false }) {
            Card(
                shape = RoundedCornerShape(12.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                modifier = Modifier.fillMaxWidth().padding(16.dp)
            ) {
                Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text("Tab Recordings", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)
                    
                    if (recordings.isEmpty()) {
                        Text("No recordings yet.", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                    } else {
                        androidx.compose.foundation.lazy.LazyColumn(modifier = Modifier.heightIn(max = 200.dp)) {
                            items(recordings) { rec ->
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(rec.label, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
                                    IconButton(onClick = { onPlayRecording(rec) }) {
                                        Icon(
                                            imageVector = if (playingRecordingId == rec.id) Icons.Default.Stop else Icons.Default.PlayArrow,
                                            contentDescription = if (playingRecordingId == rec.id) "Stop" else "Play"
                                        )
                                    }
                                }
                                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
                            }
                        }
                    }
                    
                    if (recordings.isNotEmpty()) {
                        Button(
                            onClick = {
                                onUnlink(uiState.tab)
                                showRecordings = false
                            },
                            modifier = Modifier.fillMaxWidth(),
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.errorContainer, contentColor = MaterialTheme.colorScheme.onErrorContainer)
                        ) {
                            Icon(Icons.Default.Delete, null, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(8.dp))
                            Text("Unlink Recording")
                        }
                    }
                    
                    Button(
                        onClick = { 
                            onRecordNew(uiState.tab) 
                            showRecordings = false
                        },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Icon(Icons.Default.Add, null, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(8.dp))
                        Text(if (recordings.isNotEmpty()) "Replace Recording" else "New Recording")
                    }
                    
                    if (lineRecordings.isNotEmpty()) {
                        Spacer(modifier = Modifier.height(8.dp))
                        Text("Available from Line:", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
                        androidx.compose.foundation.lazy.LazyColumn(modifier = Modifier.heightIn(max = 150.dp)) {
                            items(lineRecordings) { rec ->
                                Row(
                                    modifier = Modifier.fillMaxWidth()
                                        .clickable { 
                                            onSelectExisting(rec, uiState.tab)
                                            showRecordings = false
                                        }
                                        .padding(vertical = 4.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Text(rec.label, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
                                    Icon(Icons.Default.Add, "Select", tint = MaterialTheme.colorScheme.primary)
                                }
                                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f))
                            }
                        }
                    }
                    
                    TextButton(onClick = { showRecordings = false }, modifier = Modifier.align(Alignment.End)) {
                        Text("Close")
                    }
                }
            }
        }
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Surface(
            modifier = Modifier.fillMaxSize()
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null
                ) { /* Block touches */ },
            color = MaterialTheme.colorScheme.background
        ) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp)
                    .verticalScroll(rememberScrollState())
            ) {
                // --- Header ---
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    IconButton(onClick = onDismiss) { Icon(Icons.Default.Close, "Cancel") }

                    BasicTextField(
                        value = uiState.tab.name,
                        onValueChange = { 
                            viewModel.updateTabName(it)
                        },
                        textStyle = MaterialTheme.typography.titleMedium.copy(
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center,
                            color = MaterialTheme.colorScheme.onBackground
                        ),
                        modifier = Modifier
                            .weight(1f)
                            .padding(horizontal = 8.dp)
                            .background(MaterialTheme.colorScheme.surfaceVariant.copy(0.5f), RoundedCornerShape(4.dp))
                            .padding(4.dp),
                        singleLine = true
                    )

                    Row(verticalAlignment = Alignment.CenterVertically) {
                        if (recordings.isNotEmpty()) {
                             val isPlaying = playingRecordingId == recordings.last().id
                             IconButton(onClick = { onPlayRecording(recordings.last()) }) {
                                 Icon(
                                     imageVector = if (isPlaying) Icons.Default.Stop else Icons.Default.PlayArrow,
                                     contentDescription = if (isPlaying) "Stop Audio" else "Play Audio",
                                     tint = MaterialTheme.colorScheme.primary
                                 )
                             }
                        }
                        IconButton(onClick = { showRecordings = true }) {
                            Icon(Icons.Default.Mic, "Recordings", tint = if (recordings.isNotEmpty()) MaterialTheme.colorScheme.primary else LocalContentColor.current)
                        }
                        IconButton(onClick = { showSettings = true }) { Icon(Icons.Default.Settings, "Settings") }
                    }
                    Button(onClick = { onSave(uiState.tab) }) { Text("Save") }
                }

                HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))

                // --- Editor Area ---
                Box(
                    modifier = Modifier
                        .height(300.dp)
                        .fillMaxWidth()
                        .background(MaterialTheme.colorScheme.surfaceVariant.copy(0.1f))
                        .border(1.dp, MaterialTheme.colorScheme.outlineVariant)
                ) {
                    EditableTabDisplay(
                        tab = uiState.tab,
                        cursorColumn = uiState.cursorColumn,
                        cursorString = uiState.cursorString,
                        onTapInstruction = { col -> viewModel.jumpTo(col, 0, true) },
                        onTapNote = { col, str -> viewModel.jumpTo(col, str, false) },
                        viewModel = viewModel,
                        isFretboardMode = uiState.inputMode == TabInputMode.FRETBOARD,
                        remotePresences = uiState.remotePresences
                    )
                }

                HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp))

                Spacer(modifier = Modifier.height(8.dp))

                if (uiState.inputMode == TabInputMode.FRETBOARD && !uiState.isInstructionRowFocused) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        TabInputFretboard(
                            modifier = Modifier.fillMaxWidth(),
                            selectedNotes = uiState.fretboardSelection,
                            onNoteClick = { stringIndex, fret -> 
                                viewModel.toggleFretboardNote(stringIndex, fret)
                                val note = getTabNoteForFret(stringIndex, fret, tuning, capo)
                                if (note != "---") {
                                    onPlayNotes(listOf(note))
                                }
                            },
                            tuning = tuning,
                            capo = capo,
                            fretWindowStart = uiState.fretWindowStart,
                            onFretWindowChange = { delta -> viewModel.updateFretWindow(uiState.fretWindowStart + delta) },
                            isLeftHanded = isLeftHanded,
                            onSet = { viewModel.commitFretboardSelection() },
                            onClear = { viewModel.clearFretboardSelection() }
                        )
                    }
                    Spacer(modifier = Modifier.height(8.dp))
                }
                
                // Show EditorControlsFlattened in BOTH modes
                EditorControlsFlattened(
                    onMove = { r, c -> viewModel.moveCursor(r, c) },
                    onAddColumn = { viewModel.addColumn() },
                    onDeleteColumn = { viewModel.deleteColumn() },
                    availableChords = availableChords,
                    onInjectChord = { viewModel.injectChord(it) },
                    isInstructionFocused = uiState.isInstructionRowFocused,
                    legendItems = legendItems,
                    onInjectInstruction = { viewModel.updateInstruction(it) },
                    inputMode = uiState.inputMode,
                    onToggleInputMode = { viewModel.toggleInputMode() },
                    currentInstruction = uiState.tab.columns.getOrNull(uiState.cursorColumn)?.instruction ?: ""
                )
            }
            // --- Overlay for Restricted Members ---
            if (!canUseTabs) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(top = 70.dp) // Leave header visible but block the rest
                        .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.85f))
                        .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { },
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        modifier = Modifier.padding(24.dp)
                    ) {
                        Icon(
                            Icons.Default.Lock,
                            contentDescription = "Locked",
                            modifier = Modifier.size(48.dp),
                            tint = MaterialTheme.colorScheme.primary
                        )
                        Text(
                            "Guitar tabs are a premium feature.",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            textAlign = androidx.compose.ui.text.style.TextAlign.Center
                        )
                        Text(
                            "Upgrade your plan to create and edit guitar tabs.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            textAlign = androidx.compose.ui.text.style.TextAlign.Center
                        )
                        Button(onClick = onUpgrade) {
                            Text("Upgrade Now")
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun EditableTabDisplay(
    tab: GuitarTab,
    cursorColumn: Int,
    cursorString: Int,
    onTapInstruction: (Int) -> Unit,
    onTapNote: (Int, Int) -> Unit,
    viewModel: TabEditorViewModel,
    isFretboardMode: Boolean,
    remotePresences: List<UserPresence> = emptyList()
) {
    // Pre-calculate beat boundaries and numbers
    val beatStarts = remember(tab.subdivisions, tab.beatSubdivisions, tab.beats, tab.columns.size) {
        val starts = mutableMapOf<Int, Int>()
        val boundaries = mutableSetOf<Int>()
        var currentIndex = 0
        var beatNum = 1
        
        // Calculate boundaries for defined beats
        // Use a loop that covers enough beats
        val maxBeats = kotlin.math.max(tab.beats, (tab.columns.size / 1.coerceAtLeast(tab.subdivisions)) + 2)
        
        for (i in 0 until maxBeats) {
            val sub = tab.beatSubdivisions[i.toString()] ?: tab.subdivisions
            starts[currentIndex] = beatNum
            
            // The boundary line is drawn AFTER this beat (at start + sub)
            currentIndex += sub
            boundaries.add(currentIndex)
            beatNum++
        }
        Pair(starts, boundaries)
    }
    
    val (beatNumbers, beatLines) = beatStarts

    Row(
        modifier = Modifier
            .fillMaxSize()
            .padding(8.dp)
            .horizontalScroll(rememberScrollState())
    ) {
        // Headers
        Column(modifier = Modifier.padding(end = 8.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Box(Modifier.height(16.dp), contentAlignment = Alignment.Center) { Text("", fontSize = 10.sp) } // Beat row placeholder/label
            Box(Modifier.height(24.dp), contentAlignment = Alignment.Center) { Text("Inst", fontSize = 10.sp, color = MaterialTheme.colorScheme.primary) }
            listOf("e", "B", "G", "D", "A", "E").forEach {
                Box(Modifier.height(32.dp), contentAlignment = Alignment.Center) { Text("$it|", fontFamily = FontFamily.Monospace, fontWeight = FontWeight.Bold) }
            }
        }

        // Columns
        tab.columns.forEachIndexed { colIndex, column ->
            if (colIndex > 0 && beatLines.contains(colIndex)) {
                Box(Modifier.width(2.dp).fillMaxHeight().background(MaterialTheme.colorScheme.outline.copy(0.5f)).padding(horizontal = 4.dp))
            }

            val isColumnActive = colIndex == cursorColumn
            val columnModifier = if (isColumnActive && isFretboardMode) {
                Modifier
                    .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.1f), RoundedCornerShape(4.dp))
                    .border(1.dp, MaterialTheme.colorScheme.primary.copy(alpha = 0.5f), RoundedCornerShape(4.dp))
            } else {
                Modifier
            }

            Column(
                verticalArrangement = Arrangement.spacedBy(4.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = columnModifier.padding(horizontal = 2.dp, vertical = 4.dp)
            ) {
                // Beat Number Row
                Box(modifier = Modifier.height(16.dp), contentAlignment = Alignment.BottomCenter) {
                    if (beatNumbers.containsKey(colIndex)) {
                        Text(
                            text = "${beatNumbers[colIndex]}",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            fontWeight = FontWeight.Bold,
                            fontSize = 10.sp
                        )
                    }
                }

                // Instruction
                TabInputCell(
                    text = column.instruction,
                    isFocused = (isColumnActive && viewModel.uiState.collectAsState().value.isInstructionRowFocused),
                    isInstruction = true,
                    onValueChange = { viewModel.updateInstruction(it) },
                    onFocusRequest = { onTapInstruction(colIndex) },
                    readOnly = false, // Instructions always allow keyboard
                    remotePresences = remotePresences.filter { it.tabCursorColumn == colIndex && it.activeSectionId == "instruction" },
                    onMove = { r, c -> viewModel.moveCursor(r, c) }
                )
                // Strings
                column.notes.forEachIndexed { strIndex, note ->
                    TabInputCell(
                        text = note,
                        isFocused = (isColumnActive && strIndex == cursorString && !viewModel.uiState.collectAsState().value.isInstructionRowFocused),
                        isInstruction = false,
                        onValueChange = { viewModel.updateNote(it) },
                        onFocusRequest = { onTapNote(colIndex, strIndex) },
                        readOnly = isFretboardMode, // Block keyboard for notes if in Fretboard mode
                        remotePresences = remotePresences.filter { it.tabCursorColumn == colIndex && it.tabCursorString == strIndex && it.activeSectionId != "instruction" },
                        onMove = { r, c -> viewModel.moveCursor(r, c) }
                    )
                }
            }
        }
    }
}

@Composable
fun TabSettingsDialog(
    currentSubdivisions: Int,
    currentBeats: Int,
    onDismiss: () -> Unit,
    onConfirm: (Int, Int) -> Unit
) {
    var subs by remember { mutableStateOf(currentSubdivisions.toString()) }
    var beats by remember { mutableStateOf(currentBeats.toString()) }

    AlertDialog(
        onDismissRequest = onDismiss,
        title = { Text("Tab Settings") },
        text = {
            Column {
                OutlinedTextField(
                    value = subs,
                    onValueChange = { subs = it },
                    label = { Text("Subdivisions (slots per beat)") },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                )
                Spacer(Modifier.height(8.dp))
                OutlinedTextField(
                    value = beats,
                    onValueChange = { beats = it },
                    label = { Text("Number of Beats") },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
                )
            }
        },
        confirmButton = {
            Button(onClick = {
                onConfirm(
                    subs.trim().toIntOrNull() ?: 4,
                    beats.trim().toIntOrNull() ?: 4
                )
            }) { Text("Apply") }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancel") } }
    )
}

@Composable
fun EditorControlsFlattened(
    onMove: (Int, Int) -> Unit,
    onAddColumn: () -> Unit,
    onDeleteColumn: () -> Unit,
    availableChords: List<ChordDefinition>,
    onInjectChord: (String) -> Unit,
    isInstructionFocused: Boolean = false,
    legendItems: List<String> = emptyList(),
    onInjectInstruction: (String) -> Unit = {},
    inputMode: TabInputMode = TabInputMode.KEYBOARD,
    onToggleInputMode: () -> Unit = {},
    currentInstruction: String = ""
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(MaterialTheme.colorScheme.surface)
            .padding(8.dp)
    ) {
        if (isInstructionFocused) {
            // Expanded Instruction Editor
            OutlinedTextField(
                value = currentInstruction,
                onValueChange = onInjectInstruction,
                label = { Text("Instruction") },
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 8.dp),
                singleLine = true
            )
            
            if (legendItems.isNotEmpty()) {
                androidx.compose.foundation.lazy.LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.padding(bottom = 8.dp)
                ) {
                    items(legendItems) { text ->
                        SuggestionChip(
                            onClick = { onInjectInstruction(text) },
                            label = { Text(text) },
                            colors = SuggestionChipDefaults.suggestionChipColors(
                                containerColor = MaterialTheme.colorScheme.secondaryContainer,
                                labelColor = MaterialTheme.colorScheme.onSecondaryContainer
                            )
                        )
                    }
                }
                HorizontalDivider()
            }
        } else {
            // Row with Toggle (Left) + Chords (Scrollable)
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // --- TOGGLE BUTTON ---
                Row(
                    modifier = Modifier
                        .height(32.dp)
                        .border(
                            width = 1.dp,
                            color = MaterialTheme.colorScheme.outline,
                            shape = RoundedCornerShape(50)
                        ),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    val keyboardColors = if (inputMode == TabInputMode.KEYBOARD)
                        ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)
                    val fretboardColors = if (inputMode == TabInputMode.FRETBOARD)
                        ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)

                    Button(
                        onClick = { if (inputMode != TabInputMode.KEYBOARD) onToggleInputMode() },
                        shape = RoundedCornerShape(topStartPercent = 50, bottomStartPercent = 50),
                        modifier = Modifier
                            .fillMaxHeight()
                            .width(48.dp), // Slightly narrower
                        contentPadding = PaddingValues(0.dp),
                        colors = keyboardColors
                    ) {
                        Icon(Icons.Default.Keyboard, contentDescription = "Keyboard", modifier = Modifier.size(16.dp))
                    }

                    VerticalDivider(
                        modifier = Modifier
                            .fillMaxHeight()
                            .width(1.dp), color = MaterialTheme.colorScheme.outline
                    )

                    Button(
                        onClick = { if (inputMode != TabInputMode.FRETBOARD) onToggleInputMode() },
                        shape = RoundedCornerShape(topEndPercent = 50, bottomEndPercent = 50),
                        modifier = Modifier
                            .fillMaxHeight()
                            .width(48.dp), // Slightly narrower
                        contentPadding = PaddingValues(0.dp),
                        colors = fretboardColors
                    ) {
                        Icon(
                            painter = painterResource(Res.drawable.ic_guitar),
                            contentDescription = "Fretboard",
                            modifier = Modifier.size(16.dp)
                        )
                    }
                }

                Spacer(modifier = Modifier.width(8.dp))
                VerticalDivider(modifier = Modifier.height(32.dp))
                Spacer(modifier = Modifier.width(8.dp))

                // --- CHORD LIST ---
                if (availableChords.isNotEmpty()) {
                    androidx.compose.foundation.lazy.LazyRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        modifier = Modifier.weight(1f)
                    ) {
                        items(availableChords) { chord ->
                            SuggestionChip(
                                onClick = { onInjectChord(chord.name) },
                                label = { Text(chord.name) }
                            )
                        }
                    }
                } else {
                    Spacer(modifier = Modifier.weight(1f))
                    Text("No chords", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
            }
            HorizontalDivider()
        }

        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
            horizontalArrangement = Arrangement.SpaceEvenly,
            verticalAlignment = Alignment.CenterVertically
        ) {
            IconButton(onClick = { onMove(0, -1) }) { Icon(Icons.AutoMirrored.Filled.ArrowBack, "Left") }
            IconButton(onClick = { onMove(1, 0) }) { Icon(Icons.Default.ArrowDownward, "Down") }
            IconButton(onClick = { onMove(-1, 0) }) { Icon(Icons.Default.ArrowUpward, "Up") }
            IconButton(onClick = { onMove(0, 1) }) { Icon(Icons.AutoMirrored.Filled.ArrowForward, "Right") }
            Spacer(Modifier.width(16.dp))
            FilledIconButton(onClick = onAddColumn) { Icon(Icons.Default.Add, "Add Col") }
            IconButton(onClick = onDeleteColumn) { Icon(Icons.Default.Delete, "Del Col", tint = MaterialTheme.colorScheme.error) }
        }
    }
}

@Composable
fun TabInputCell(
    text: String,
    isFocused: Boolean,
    isInstruction: Boolean,
    onValueChange: (String) -> Unit,
    onFocusRequest: () -> Unit,
    readOnly: Boolean,
    remotePresences: List<UserPresence> = emptyList(),
    onMove: ((Int, Int) -> Boolean)? = null
) {
    val focusRequester = remember { FocusRequester() }
    // Instructions are smaller and thinner
    val height = if (isInstruction) 24.dp else 32.dp
    val minWidth = 36.dp
    val fontSize = if (isInstruction) 10.sp else 16.sp

    LaunchedEffect(isFocused) { if (isFocused) focusRequester.requestFocus() }

    Box(
        modifier = Modifier
            .height(height)
            .width(androidx.compose.foundation.layout.IntrinsicSize.Min)
            .clickable { onFocusRequest() }
            .background(
                if (isFocused) MaterialTheme.colorScheme.primaryContainer else Color.Transparent,
                RoundedCornerShape(4.dp)
            )
            .drawWithContent {
                drawContent()
                remotePresences.forEachIndexed { index, presence ->
                    val color = Color(presence.color)
                    val strokeWidth = 2.dp.toPx()
                    
                    // Draw a small indicator for each remote user
                    val radius = 3.dp.toPx()
                    val padding = 4.dp.toPx()
                    drawCircle(
                        color = color,
                        radius = radius,
                        center = Offset(size.width - padding - (index * radius * 2.5f), padding)
                    )
                    
                    // Also draw a colored border
                    drawRoundRect(
                        color = color.copy(alpha = 0.5f),
                        size = size,
                        cornerRadius = CornerRadius(4.dp.toPx()),
                        style = Stroke(width = strokeWidth)
                    )
                }
            }
            .border(
                width = if (isFocused) 2.dp else 0.dp,
                color = if (isFocused) MaterialTheme.colorScheme.primary else Color.Transparent,
                RoundedCornerShape(4.dp)
            )
            .padding(horizontal = 4.dp),
        contentAlignment = Alignment.Center
    ) {
        if (isFocused) {
            val editingText = if (!isInstruction && text == "-") "" else text

            BasicTextField(
                value = TextFieldValue(text = editingText, selection = TextRange(editingText.length)),
                onValueChange = {
                    if (!readOnly) {
                        val newValue = if (isInstruction) it.text else it.text.ifEmpty { "-" }
                        onValueChange(newValue)
                    }
                },
                modifier = Modifier
                    .focusRequester(focusRequester)
                    .width(minWidth)
                    .onKeyEvent { event ->
                        if (event.type == KeyEventType.KeyDown) {
                            when (event.key) {
                                Key.DirectionUp -> {
                                    onMove?.invoke(-1, 0) ?: false
                                }
                                Key.DirectionDown -> {
                                    onMove?.invoke(1, 0) ?: false
                                }
                                Key.DirectionLeft -> {
                                    onMove?.invoke(0, -1) ?: false
                                }
                                Key.DirectionRight -> {
                                    onMove?.invoke(0, 1) ?: false
                                }
                                else -> false
                            }
                        } else {
                            false
                        }
                    },
                textStyle = TextStyle(
                    fontFamily = FontFamily.Monospace,
                    fontSize = fontSize,
                    textAlign = TextAlign.Center,
                    color = MaterialTheme.colorScheme.onPrimaryContainer
                ),
                keyboardOptions = KeyboardOptions(
                    keyboardType = if (isInstruction) KeyboardType.Text else KeyboardType.Ascii,
                    imeAction = ImeAction.Next
                ),
                singleLine = true,
                readOnly = readOnly
            )
        } else {
            Text(
                text = text,
                style = TextStyle(
                    fontFamily = FontFamily.Monospace,
                    fontSize = fontSize,
                    textAlign = TextAlign.Center,
                    color = MaterialTheme.colorScheme.onSurface
                ),
                modifier = Modifier.width(minWidth)
            )
        }
    }
}

// --- Helper for Fretboard ---
private fun getTabNoteForFret(stringIndex: Int, fret: Any, tuning: List<String>, capo: Int): String {
    if (fret !is Int || fret < 0) return "---"
    // stringIndex 0 is Top (High E), but tuning[0] is Low E. So we flip.
    val tuningIndex = (tuning.size - 1) - stringIndex
    val openNote = tuning.getOrNull(tuningIndex) ?: return "---"
    val openMidi = songwritersnotebook.app.utils.ChordUtils.noteNameToMidi(openNote) ?: return "---"
    val finalMidi = if (fret == 0) {
        openMidi + capo
    } else {
        openMidi + fret
    }
    return songwritersnotebook.app.utils.ChordUtils.midiToNoteName(finalMidi)
}

@Composable
fun TabInputFretboard(
    modifier: Modifier = Modifier,
    selectedNotes: Map<Int, Int>, // StringIndex -> FretNumber
    onNoteClick: (Int, Int) -> Unit, // (StringIndex 0-5, FretNumber)
    tuning: List<String>,
    capo: Int,
    fretWindowStart: Int,
    onFretWindowChange: (Int) -> Unit, // Delta
    isLeftHanded: Boolean = false,
    onSet: () -> Unit = {},
    onClear: () -> Unit = {}
) {
    val numFretsToShow = 5
    val primaryColor = MaterialTheme.colorScheme.primary
    val reversedTuning = remember(tuning) { tuning.reversed() }
    val fretboardHeight = 180.dp

    Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = modifier) {
        val layoutDirection = if (isLeftHanded) androidx.compose.ui.unit.LayoutDirection.Rtl else androidx.compose.ui.unit.LayoutDirection.Ltr
        androidx.compose.runtime.CompositionLocalProvider(androidx.compose.ui.platform.LocalLayoutDirection provides layoutDirection) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Tuning Labels on the LEFT
                androidx.compose.runtime.CompositionLocalProvider(androidx.compose.ui.platform.LocalLayoutDirection provides androidx.compose.ui.unit.LayoutDirection.Ltr) {
                    Column(
                        modifier = Modifier.height(fretboardHeight),
                        verticalArrangement = Arrangement.SpaceEvenly,
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        reversedTuning.forEach { note ->
                            Text(
                                note.dropLast(1),
                                textAlign = TextAlign.Center,
                                modifier = Modifier.width(24.dp),
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontWeight = FontWeight.Bold
                            )
                        }
                    }
                }

                // Open String Toggles (Fret 0)
                Column(
                    modifier = Modifier
                        .height(fretboardHeight)
                        .padding(horizontal = 4.dp),
                    verticalArrangement = Arrangement.SpaceEvenly,
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    (0..5).forEach { stringIndex ->
                        val selection = selectedNotes[stringIndex]
                        Box(
                            modifier = Modifier
                                .size(24.dp)
                                .clickable {
                                    onNoteClick(stringIndex, 0)
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            when (selection) {
                                0 -> {
                                    // Open String (Blue)
                                    Box(
                                        modifier = Modifier
                                            .size(12.dp)
                                            .background(primaryColor, androidx.compose.foundation.shape.CircleShape)
                                    )
                                }
                                -1 -> {
                                    // Muted String (X)
                                    Icon(
                                        Icons.Default.Close,
                                        contentDescription = "Muted",
                                        modifier = Modifier.size(16.dp),
                                        tint = MaterialTheme.colorScheme.error
                                    )
                                }
                                else -> {
                                    // Unselected (Empty Ring)
                                    Box(
                                        modifier = Modifier
                                            .size(12.dp)
                                            .border(
                                                1.dp,
                                                MaterialTheme.colorScheme.onSurfaceVariant,
                                                androidx.compose.foundation.shape.CircleShape
                                            )
                                    )
                                }
                            }
                        }
                    }
                }

                // Fretboard Canvas
                androidx.compose.foundation.Canvas(modifier = Modifier
                    .weight(1f)
                    .height(fretboardHeight)
                    .pointerInput(isLeftHanded, fretWindowStart) {
                        val canvasSize = size
                        detectTapGestures { offset ->
                            val stringHeight = canvasSize.height / 6f
                            val stringIndex = (offset.y / stringHeight).toInt().coerceIn(0, 5)

                            // INVERT X for Left Handed Mode
                            val effectiveX = if (isLeftHanded) canvasSize.width - offset.x else offset.x

                            val fretWidth = canvasSize.width / numFretsToShow.toFloat()
                            val fretIndex = (effectiveX / fretWidth).toInt().coerceIn(0, numFretsToShow - 1)
                            val fretNumber = fretWindowStart + fretIndex + 1

                            if (fretNumber <= capo) return@detectTapGestures

                            onNoteClick(stringIndex, fretNumber)
                        }
                    }
                ) {
                    val numStrings = 6
                    val stringHeight = size.height / numStrings.toFloat()
                    val fretWidth = size.width / numFretsToShow.toFloat()

                    drawRect(color = Color(0xFF525252))
                    (0..numFretsToShow).forEach { fret ->
                        val rawX = fret * fretWidth
                        // INVERT X for Left Handed Mode
                        val x = if (isLeftHanded) size.width - rawX else rawX

                        val stroke = if (fret == 0 && capo == 0) 6f else 2f
                        drawLine(
                            Color.Gray,
                            start = Offset(x, 0f),
                            end = Offset(x, size.height),
                            strokeWidth = stroke
                        )
                    }

                    (0 until numStrings).forEach { string ->
                        val y = (string + 0.5f) * stringHeight
                        drawLine(
                            Color.LightGray,
                            start = Offset(0f, y),
                            end = Offset(size.width, y),
                            strokeWidth = (string * 0.4f) + 1.5f
                        )
                    }

                    if (capo > 0 && capo >= fretWindowStart && capo < fretWindowStart + numFretsToShow) {
                        val capoFretPosIndex = capo - fretWindowStart
                        val rawXCenter = (capoFretPosIndex * fretWidth) - (fretWidth / 2f)
                        val xCenter = if (isLeftHanded) size.width - rawXCenter else rawXCenter

                        val barWidth = 12.dp.toPx()
                        drawRoundRect(
                            color = primaryColor.copy(alpha = 0.9f),
                            topLeft = Offset((xCenter - (barWidth / 2)).toFloat(), -4.dp.toPx()),
                            size = Size(barWidth, size.height + 8.dp.toPx()),
                            cornerRadius = CornerRadius(6.dp.toPx())
                        )
                    }

                    val markerFrets = listOf(3, 5, 7, 9, 12, 15, 17, 19, 21)
                    (0 until numFretsToShow).forEach { fretIndex ->
                        val fretNumber = fretWindowStart + fretIndex + 1
                        if (markerFrets.contains(fretNumber)) {
                            val rawX = (fretIndex * fretWidth) + (fretWidth / 2f)
                            val x = if (isLeftHanded) size.width - rawX else rawX

                            val isDoubleDot = fretNumber % 12 == 0
                            if (isDoubleDot) {
                                drawCircle(
                                    Color.Gray,
                                    radius = 4.dp.toPx(),
                                    center = Offset(x, size.height * 0.25f)
                                )
                                drawCircle(
                                    Color.Gray,
                                    radius = 4.dp.toPx(),
                                    center = Offset(x, size.height * 0.75f)
                                )
                            } else {
                                drawCircle(
                                    Color.Gray,
                                    radius = 4.dp.toPx(),
                                    center = Offset(x, size.height / 2f)
                                )
                            }
                        }
                    }

                    // --- DRAW SELECTED NOTES ---
                    selectedNotes.forEach { (stringIndex, fret) ->
                        if (fret > 0) {
                            if (fret > fretWindowStart && fret <= fretWindowStart + numFretsToShow) {
                                val fretPosIndex = fret - fretWindowStart - 1
                                val rawX = (fretPosIndex * fretWidth) + (fretWidth / 2f)
                                val x = if (isLeftHanded) size.width - rawX else rawX
                                
                                val y = (stringIndex + 0.5f) * stringHeight
                                
                                drawCircle(
                                    color = primaryColor,
                                    radius = stringHeight * 0.35f,
                                    center = Offset(x, y)
                                )
                            }
                        }
                    }
                }
            }
        }

        // Fret window controls (Keep LTR)
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            // Wrap existing controls in a Row to keep them centered
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.Center
            ) {
                // LEFT BUTTON
                if (isLeftHanded) {
                    IconButton(onClick = { onFretWindowChange(1) }, enabled = fretWindowStart < 18) {
                        Icon(Icons.Default.ChevronLeft, "Shift Left (Higher Frets)")
                    }
                } else {
                    IconButton(onClick = { onFretWindowChange(-1) }, enabled = fretWindowStart > 0) {
                        Icon(Icons.Default.ChevronLeft, "Shift Left (Lower Frets)")
                    }
                }

                Text(
                    "Fret: ${fretWindowStart + 1}",
                    fontFamily = FontFamily.Monospace,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(horizontal = 4.dp)
                )

                // RIGHT BUTTON
                if (isLeftHanded) {
                    IconButton(onClick = { onFretWindowChange(-1) }, enabled = fretWindowStart > 0) {
                        Icon(Icons.Default.ChevronRight, "Shift Right (Lower Frets)")
                    }
                } else {
                    IconButton(onClick = { onFretWindowChange(1) }, enabled = fretWindowStart < 18) {
                        Icon(Icons.Default.ChevronRight, "Shift Right (Higher Frets)")
                    }
                }
            }
            
            // Set / Clear Buttons (Right aligned)
            Row(verticalAlignment = Alignment.CenterVertically) {
                 Button(onClick = onSet) { Text("Set") }
                 Spacer(Modifier.width(8.dp))
                 TextButton(onClick = onClear) { Text("Clear") }
            }
        }
    }
}
